import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';

export function EliteScanBar({ className }: { className?: string }) {
    return (
        <div className={cn("w-full h-[2px] bg-white/[0.03] overflow-hidden relative", className)}>
            <div className="absolute top-0 bottom-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-gold-500 to-transparent animate-scan" />
        </div>
    );
}

export function EliteAngularLoader({ className }: { className?: string }) {
    return (
        <div className={cn("relative flex items-center justify-center", className)}>
            <div className="absolute inset-0 border-2 border-gold-500 animate-angular-spin shadow-[0_0_15px_rgba(212,175,55,0.3)] pointer-events-none" />
            <div className="absolute inset-2 border border-gold-500/30 animate-angular-spin" style={{ animationDirection: 'reverse', animationDuration: '6s' }} />
        </div>
    );
}

export function ElitePulseState({ children, isActive = false, className }: { children: React.ReactNode; isActive?: boolean; className?: string }) {
    return (
        <div className={cn(
            "relative transition-all duration-700 rounded-2xl",
            isActive && "animate-pulse-glow shadow-[0_0_20px_rgba(212,175,55,0.2)] border-gold-500/30",
            !isActive && "border-white/5",
            className
        )}>
            {children}
        </div>
    );
}

const DEFAULT_MESSAGES = [
    "INITIALIZING ANALYSIS",
    "CALIBRATING PERFORMANCE METRICS",
    "ANALYZING SHOT SEQUENCES",
    "EVALUATING CONTROL TIME",
    "GENERATING MATCH INTELLIGENCE",
    "SYNTHESIZING TRAINING RECOMMENDATIONS"
];

export function EliteProcessingOverlay({
    isVisible = false,
    messages = DEFAULT_MESSAGES
}: {
    isVisible: boolean;
    messages?: string[];
}) {
    const [msgIndex, setMsgIndex] = useState(0);

    useEffect(() => {
        if (!isVisible) {
            setMsgIndex(0);
            return;
        }
        const interval = setInterval(() => {
            setMsgIndex(prev => (prev + 1) % messages.length);
        }, 2500);
        return () => clearInterval(interval);
    }, [isVisible, messages.length]);

    if (!isVisible) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-xl animate-fade-in" aria-live="polite">
            <div className="bg-bg-surface border border-white/10 shadow-card-elite rounded-2xl p-8 max-w-sm w-full mx-4 flex flex-col items-center animate-fade-in-up relative overflow-hidden">
                <EliteScanBar className="absolute top-0 left-0 right-0" />

                <div className="my-8">
                    <EliteAngularLoader className="w-12 h-12" />
                </div>

                <div className="h-6 flex items-center justify-center w-full mt-2">
                    <p
                        key={msgIndex}
                        className="text-[10px] font-black uppercase tracking-widest text-text-muted text-center animate-fade-in-up"
                    >
                        {messages[msgIndex]}
                    </p>
                </div>
            </div>
        </div>
    );
}
